import {
	parseDate,
	formatToStandardDate,
	Numbers,
	properCase,
	upperCase,
	lowerCase,
	sentenceCase,
	camelCase,
	slugify,
	truncate,
} from '@buddy-technology/buddy_helpers';

// --- Helpers ---

const toDateObj = (value) => {
	if (value instanceof Date && !Number.isNaN(value.getTime())) {
		return value;
	}
	return parseDate(value);
};

const normalizePhone = (value) => {
	const digits = String(value).replace(/\D/g, '');
	if (digits.length === 11 && digits[0] === '1') {
		return digits.slice(1);
	}
	if (digits.length === 10) {
		return digits;
	}
	return null;
};

// --- String filters ---

const upper = (value) => upperCase(String(value));
const lower = (value) => lowerCase(String(value));
const title = (value) => properCase(String(value));
const sentence = (value) => sentenceCase(String(value));
const camel = (value) => camelCase(String(value));
const slug = (value) => slugify(String(value));
const truncateFilter = (value, maxChars = '100', append = '...') => truncate(String(value), Number(maxChars), append, false);

// --- Default filter ---

const defaultFilter = (value, fallback = '') => {
	if (value === undefined || value === null || value === '') {
		return fallback;
	}
	return value;
};

// --- Date filters ---
// All accept ISO strings (YYYY-MM-DD), standard date strings (MM/DD/YYYY), or Date objects.
// Returns '' if the value cannot be parsed.

const date = (value) => {
	const parsed = toDateObj(value);
	if (!parsed) {
		return '';
	}
	return formatToStandardDate(parsed);
};

const date_iso = (value) => {
	const parsed = toDateObj(value);
	if (!parsed) {
		return '';
	}
	const y = parsed.getFullYear();
	const mm = String(parsed.getMonth() + 1).padStart(2, '0');
	const dd = String(parsed.getDate()).padStart(2, '0');
	return `${y}-${mm}-${dd}`;
};

const date_long = (value) => {
	const parsed = toDateObj(value);
	if (!parsed) {
		return '';
	}
	return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(parsed);
};

const date_short = (value) => {
	const parsed = toDateObj(value);
	if (!parsed) {
		return '';
	}
	return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(parsed);
};

// --- Phone filters (US-based) ---
// Strips non-digits, handles optional leading country code '1'.
// Returns the original value unchanged if the number is not a valid 10-digit US number.

const phone = (value) => {
	const d = normalizePhone(value);
	if (!d) {
		return value;
	}
	return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const phone_dots = (value) => {
	const d = normalizePhone(value);
	if (!d) {
		return value;
	}
	return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
};

const phone_dashes = (value) => {
	const d = normalizePhone(value);
	if (!d) {
		return value;
	}
	return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};

// --- Number filters ---

const usd = (value) => Numbers.toUSD(Number(value));
const percentage = (value) => Numbers.toPercentage(Number(value));
const number = (value) => Numbers.toDisplay(Number(value));
const round = (value, digits = '0') => Numbers.roundTo(Number(value), Number(digits));

// Locale-aware currency via Intl (for non-USD or non-en-US scenarios)
const currency = (value, currencyCode = 'USD', locale = 'en-US') => {
	try {
		const formatter = new Intl.NumberFormat(
			locale,
			{
				style: 'currency',
				currency: currencyCode,
			},
		);

		return formatter.format(Number(value));
	} catch (error) {
		return value;
	}
};

// --- Registry ---

const registry = {
	upperCase: upper,
	lowerCase: lower,
	titleCase: title,
	sentenceCase: sentence,
	camelCase: camel,
	slug,
	truncate: truncateFilter,
	default: defaultFilter,
	date,
	date_iso,
	date_long,
	date_short,
	phone,
	phone_dots,
	phone_dashes,
	usd,
	percentage,
	number,
	round,
	currency,
};

/**
 * Applies a list of parsed filters left-to-right to a value.
 * Unknown filter names are silently skipped and the value passes through.
 * @param {*} value
 * @param {Array<{ name: string, args: string[] }>} filters
 * @returns {*}
 */
export const applyFilters = (value, filters) => {
	if (!filters || !filters.length) {
		return value;
	}
	return filters.reduce((out, { name, args }) => {
		const fn = registry[name];
		if (!fn) {
			return out;
		}
		return fn(out, ...args);
	}, value);
};

export default registry;
